import type { SpecType } from '@local/lib';

import { Button } from '@salesforce/design-system-react';
import { useShallow } from 'zustand/react/shallow';

import type { TemplateSpecConfig } from '../qualityFormulaTypes';

import { LoadTemplateControl } from '../../templates/components/LoadTemplateControl';
import { SPEC_TYPES } from '../qualityFormulaMetadata';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { PlaygroundPanel } from './PlaygroundPanel';
import { QualitySpecCard } from './QualitySpecCard';
import './qualitySpecsWorkbench.css';
import { SpecNavigator } from './SpecNavigator';
import { TemplateManagerPanel } from './TemplateManagerPanel';

// Stable across every render (no closure over page state) so it never invalidates the focused
// `QualitySpecCard`'s `memo` — see `QualityFormulasPage`'s previous identically-reasoned constant.
const renderLoadTemplateControl = (
  specType: SpecType,
  specLabel: string,
  onApply: (config: TemplateSpecConfig) => void,
): React.ReactNode => <LoadTemplateControl onApply={onApply} specLabel={specLabel} specType={specType} />;

interface EmptyEditorStateProps {
  onAddFirstSpec: () => void;
}

const EmptyEditorState: React.FC<EmptyEditorStateProps> = ({ onAddFirstSpec }) => (
  <div className="qfw-editor qfw-empty-state">
    <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-2)' }}>
      <h2 className="slds-text-heading_small">No quality specs configured yet</h2>
      <p className="slds-text-body_regular slds-text-color_weak slds-text-align_center" style={{ maxWidth: 420 }}>
        Add a spec from the list on the left to start building its price or quantity adjustment formula.
      </p>
      <Button
        className="slds-m-top_small"
        label="Add your first spec"
        onClick={onAddFirstSpec}
        variant="outline-brand"
      />
    </div>
  </div>
);

interface QualitySpecsWorkbenchProps {
  /** Contract Entry's "Reset" button sits next to the Playground's Test button. */
  extraActions?: React.ReactNode;
  lineItem?: boolean;
}

/**
 * Replaces the old quality-type filter + stacked spec card list with a navigator and one focused
 * editor or Playground panel. Switching between specs and the Playground is purely a
 * presentation change: spec configurations and test values stay in the store when unmounted.
 *
 * The grid stacks the navigator above the editor on narrow screens and uses two columns once its
 * container is wide enough. Container queries follow the embedded app's available width rather
 * than the host viewport (see qualitySpecsWorkbench.css).
 */
export const QualitySpecsWorkbench: React.FC<QualitySpecsWorkbenchProps> = ({ extraActions, lineItem = false }) => {
  const { addSpec, isPlaygroundOpen, isTemplateManagerOpen, selectedSpecType, specStates } = useQualityFormulaStore(
    useShallow(store => ({
      addSpec: store.addSpec,
      isPlaygroundOpen: store.isPlaygroundOpen,
      isTemplateManagerOpen: store.isTemplateManagerOpen,
      selectedSpecType: store.selectedSpecType,
      specStates: store.specStates,
    })),
  );

  const selectedSpec = SPEC_TYPES.find(spec => spec.key === selectedSpecType);
  const firstAvailableSpec = SPEC_TYPES.find(spec => !specStates[spec.key].enabled);
  let editor: React.ReactNode = null;
  if (!isPlaygroundOpen && !isTemplateManagerOpen) {
    if (selectedSpec) {
      editor = (
        <div className="qfw-editor" id={`spec-card-${selectedSpec.key}`}>
          <QualitySpecCard
            hint={selectedSpec.hint}
            key={selectedSpec.key}
            renderLoadTemplateControl={renderLoadTemplateControl}
            specLabel={selectedSpec.label}
            specType={selectedSpec.key}
            variant="focused"
          />
        </div>
      );
    } else if (lineItem) {
      editor = (
        <p className="qfw-editor slds-text-body_regular slds-text-color_weak slds-p-around_medium">
          Choose a spec from the list to configure its formula.
        </p>
      );
    } else {
      editor = (
        <EmptyEditorState
          onAddFirstSpec={() => {
            if (firstAvailableSpec) addSpec(firstAvailableSpec.key);
          }}
        />
      );
    }
  }

  return (
    <section
      aria-label="Configure quality specifications"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-3)', minHeight: 0 }}
    >
      {lineItem ? null : (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--slds-g-spacing-3)',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              alignItems: 'baseline',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--slds-g-spacing-2)',
              maxWidth: 760,
            }}
          >
            <h2 className="slds-text-heading_small">Configure Quality Specs</h2>
            <span className="slds-text-body_regular slds-text-color_weak">
              Pick a spec to edit its formula, then test every enabled spec together in the Playground.
            </span>
          </div>
        </div>
      )}

      {/* Separates the page-level header from the nav/editor/Playground grid, matching the
          Ash/Sulfur/BTU references' own full-width rule there. */}
      {lineItem ? null : <hr className="qfw-divider" />}
      <div className="qfw-container">
        <div
          className="qfw-grid"
          data-line-item={lineItem}
          data-playground-open={isPlaygroundOpen || isTemplateManagerOpen}
        >
          <SpecNavigator showManageTemplates={!lineItem} />
          {editor}
          {isPlaygroundOpen ? (
            <PlaygroundPanel
              extraActions={extraActions}
              showResults={lineItem}
              testButtonLabel={lineItem ? 'Run Test' : undefined}
              workbench
            />
          ) : null}
          {isTemplateManagerOpen ? <TemplateManagerPanel /> : null}
        </div>
      </div>
    </section>
  );
};
